import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Maximize, Send, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { useFetch } from "@/lib/hooks";
import { usePageSeo } from "@/lib/seo";
import { api, errMsg } from "@/lib/api";
import { PhotoImg } from "@/components/site/PhotoImg";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// wallCm: real-world width covered by the room photo; cx/cy: frame centre (%); maxBottom: lowest allowed frame edge (%)
export const ROOMS = {
  living: { img: "/rooms/living.jpg", wallCm: 470, cx: 51, cy: 38, maxBottom: 66 },
  office: { img: "/rooms/office.jpg", wallCm: 385, cx: 52, cy: 36, maxBottom: 62 },
  bedroom: { img: "/rooms/bedroom.jpg", wallCm: 400, cx: 51, cy: 32, maxBottom: 56 },
  artgallery: { img: "/rooms/gallery.jpg", wallCm: 560, cx: 50, cy: 40, maxBottom: 78 },
};
export const FRAMES = {
  black: { color: "#141414", cm: 3, inner: "#000" },
  white: { color: "#f3f1ec", cm: 3, inner: "#ddd" },
  wood: { color: "linear-gradient(135deg,#a77b4f,#8a6038 45%,#b98c5e 70%,#7d5531)", cm: 3.5, inner: "#5e4024" },
  none: { color: "transparent", cm: 0, inner: "transparent" },
};
export const SIZES = [[30, 45], [40, 60], [60, 90], [80, 120]];
const ROOM_W = 1264;
const ROOM_H = 848;

const Option = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`border px-4 py-3 text-left text-sm transition-colors duration-300 ${active ? "border-white bg-white text-black" : "border-white/15 text-white/80 hover:border-white/50"}`}>{children}</button>
);

const Scene = ({ photo, room, frame, size, mat }) => {
  const R = ROOMS[room];
  const F = FRAMES[frame];
  const v = photo?.variants?.display || photo?.variants?.medium;
  const ratio = v ? v.width / v.height : 1.5;
  const long = size[1];
  const imgW = ratio >= 1 ? long : long * ratio;
  const imgH = ratio >= 1 ? long / ratio : long;
  const matCm = mat && frame !== "none" ? Math.max(5, long * 0.07) : 0;
  const outerW = imgW + 2 * (matCm + F.cm);
  const outerH = imgH + 2 * (matCm + F.cm);
  const wPct = (outerW / R.wallCm) * 100;
  const hPct = (outerH / R.wallCm) * 100 * (ROOM_W / ROOM_H);
  const top = Math.min(R.cy - hPct / 2, R.maxBottom - hPct);
  const pad = (cm) => `${(cm / R.wallCm) * 100}%`;
  return (
    <div className="relative w-full overflow-hidden bg-[#111]" style={{ aspectRatio: `${ROOM_W}/${ROOM_H}` }} data-testid="frame-scene">
      <img src={R.img} alt={room} className="absolute inset-0 h-full w-full object-cover" />
      <motion.div layout transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }} data-testid="frame-artwork"
        className="absolute" style={{ left: `${R.cx - wPct / 2}%`, top: `${Math.max(top, 3)}%`, width: `${wPct}%`, aspectRatio: `${outerW}/${outerH}`,
          background: F.color, padding: pad(F.cm), boxShadow: frame === "none" ? "0 18px 30px rgba(0,0,0,.35)" : "0 22px 40px rgba(0,0,0,.45), 0 4px 10px rgba(0,0,0,.3)" }}>
        <div className="relative h-full w-full" style={{ background: matCm ? "#f6f4ef" : "transparent", padding: matCm ? `${(matCm / (outerW - 2 * F.cm)) * 100}%` : 0, boxShadow: F.cm ? `inset 0 0 0 1px ${F.inner}` : "none" }}>
          <div className="relative h-full w-full" style={{ boxShadow: matCm ? "inset 0 1px 3px rgba(0,0,0,.35)" : "none" }}>
            <PhotoImg photo={photo} sizes="60vw" className="h-full w-full" />
          </div>
          {frame !== "none" && <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent" />}
        </div>
      </motion.div>
    </div>
  );
};

const QuoteDialog = ({ open, setOpen, summary, photo }) => {
  const { t } = useLang();
  const [f, setF] = useState({ name: "", email: "", message: "" });
  const send = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", { ...f, subject: `Print quote — ${summary}`, kind: "print-quote", photo_id: photo?.id, message: `${summary}\n\n${f.message || "-"}` });
      toast.success(t("sent")); setOpen(false);
    } catch (er) { toast.error(errMsg(er)); }
  };
  const input = "w-full border border-white/15 bg-transparent px-4 py-3 text-sm text-white outline-none focus:border-white";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="border-white/10 bg-[#0f0f0f] text-white" data-testid="quote-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{t("quote")}</DialogTitle></DialogHeader>
        <p className="font-mono text-xs text-white/60">{summary}</p>
        <form onSubmit={send} className="space-y-3">
          <input required placeholder={t("name")} className={input} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="quote-name-input" />
          <input required type="email" placeholder="Email" className={input} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="quote-email-input" />
          <textarea rows={3} placeholder={t("message")} className={input} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="quote-message-input" />
          <button className="btn-line w-full justify-center" data-testid="quote-submit-button"><Send size={14} /> {t("send")}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function FrameVisualizer() {
  const { t, L } = useLang();
  usePageSeo("frame");
  const [params, setParams] = useSearchParams();
  const { data } = useFetch("/photos?limit=200");
  const photos = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const [room, setRoom] = useState("living");
  const [frame, setFrame] = useState("black");
  const [size, setSize] = useState(SIZES[2]);
  const [mat, setMat] = useState(true);
  const [full, setFull] = useState(false);
  const [quote, setQuote] = useState(false);
  const stage = useRef(null);
  const photo = photos.find((p) => p.slug === params.get("photo")) || photos.find((p) => p.featured) || photos[0];
  const v = photo?.variants?.display;
  const ratio = v ? v.width / v.height : 1.5;
  const dims = ratio >= 1 ? [size[1], size[1] / ratio] : [size[1] * ratio, size[1]];
  const summary = `${L(photo, "title")} · ${dims[0].toFixed(1)} × ${dims[1].toFixed(1)} cm · ${t(frame)}${mat && frame !== "none" ? ` · ${t("mat")}` : ""}`;
  useEffect(() => {
    if (!full) return undefined;
    window.__lenis?.stop();
    const k = (e) => { if (e.key === "Escape") setFull(false); };
    window.addEventListener("keydown", k);
    return () => { window.removeEventListener("keydown", k); window.__lenis?.start(); };
  }, [full]);
  return (
    <div data-testid="frame-visualizer-page" className="mx-auto max-w-[1600px] px-5 pb-24 pt-32 md:px-10 md:pt-36">
      <div className="mb-10 max-w-3xl">
        <div className="kicker mb-4 flex items-center gap-3"><span className="h-px w-10 bg-[#5d9a35]" />{t("prints")}</div>
        <h1 className="font-display text-5xl font-extrabold leading-[0.92] text-white sm:text-7xl">{t("frame")}</h1>
        <p className="mt-5 max-w-xl text-white/70">{t("printsLead")}</p>
      </div>
      <div className="grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <div className="lg:sticky lg:top-24" ref={stage}>
            {photo && <Scene photo={photo} room={room} frame={frame} size={size} mat={mat} />}
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div className="font-mono text-xs text-white/70" data-testid="frame-summary">{t("imageArea")}: {dims[0].toFixed(1)} × {dims[1].toFixed(1)} cm · {ratio.toFixed(2)}:1</div>
              <button onClick={() => setFull(true)} data-testid="frame-fullscreen-button" className="btn-line !py-3"><Maximize size={14} /> {t("fullscreen")}</button>
            </div>
          </div>
        </div>
        <div className="space-y-9 lg:col-span-4">
          <div>
            <div className="kicker mb-3">{t("room")}</div>
            <div className="grid grid-cols-2 gap-2">{Object.keys(ROOMS).map((r) => <Option key={r} active={room === r} onClick={() => setRoom(r)} testid={`frame-room-selector-${r}`}>{t(r)}</Option>)}</div>
          </div>
          <div>
            <div className="kicker mb-3">{t("frameStyle")}</div>
            <div className="grid grid-cols-2 gap-2">
              {Object.keys(FRAMES).map((f) => (
                <Option key={f} active={frame === f} onClick={() => setFrame(f)} testid={`frame-material-${f}`}>
                  <span className="flex items-center gap-3"><span className="h-4 w-4 border border-white/30" style={{ background: FRAMES[f].color }} />{t(f)}</span>
                </Option>
              ))}
            </div>
            <label className="mt-3 flex cursor-pointer items-center gap-3 text-sm text-white/80">
              <input type="checkbox" checked={mat} onChange={(e) => setMat(e.target.checked)} data-testid="frame-mat-toggle" className="h-4 w-4 accent-[#5d9a35]" /> {t("mat")}
            </label>
          </div>
          <div>
            <div className="kicker mb-3">{t("size")}</div>
            <div className="grid grid-cols-2 gap-2">{SIZES.map((s) => <Option key={s.join()} active={size === s} onClick={() => setSize(s)} testid={`frame-size-${s[0]}x${s[1]}`}><span className="font-mono">{s[0]} × {s[1]} cm</span></Option>)}</div>
          </div>
          <div>
            <div className="kicker mb-3">{t("photo")}</div>
            <div className="grid max-h-[300px] grid-cols-4 gap-2 overflow-y-auto pr-1" data-lenis-prevent>
              {photos.map((p) => (
                <button key={p.id} onClick={() => setParams({ photo: p.slug }, { replace: true })} data-testid={`frame-photo-${p.slug}`}
                  className={`relative aspect-square overflow-hidden border-2 transition-colors ${p.id === photo?.id ? "border-white" : "border-transparent opacity-70 hover:opacity-100"}`}>
                  <PhotoImg photo={p} sizes="80px" className="h-full w-full" />
                </button>
              ))}
            </div>
          </div>
          <button onClick={() => setQuote(true)} data-testid="frame-quote-button" className="btn-line w-full justify-center"><Send size={14} /> {t("quote")}</button>
        </div>
      </div>
      {full && photo && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black p-2 md:p-8" data-testid="frame-fullscreen-preview">
          <button onClick={() => setFull(false)} data-testid="frame-fullscreen-close" className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center border border-white/20 bg-black/50 text-white"><X /></button>
          <div className="w-full max-w-[min(100%,calc((100vh-4rem)*1.49))]"><Scene photo={photo} room={room} frame={frame} size={size} mat={mat} /></div>
        </div>
      )}
      <QuoteDialog open={quote} setOpen={setQuote} summary={summary} photo={photo} />
    </div>
  );
}
